/**
 * Jitsi provider — EMBEDDED live class (External API iframe).
 *
 * DETERMINISTIC room `spaceacademy-{sessionId}-{hash}` with server salt;
 * configurable domain (default `meet.jit.si`). The embedded component is
 * lazy-loaded (F3-G) to avoid pulling the SDK into the base bundle.
 */

import {
  DEFAULT_JITSI_DOMAIN,
  jitsiRoomName,
  type JoinDescriptor,
  type JoinUser,
  type LiveSession,
  type MeetingProvider,
  type MeetingProviderManifest,
} from "../registry";

const manifest: MeetingProviderManifest = {
  id: "jitsi",
  name: "Jitsi Meet",
  description: "Clase en vivo embebida (iframe) con sala determinista; sin credenciales.",
  capabilities: {
    embeddable: true,
    attendanceCallback: true, // iframe API events (participantJoined/Left)
    recordingFetch: false, // v1: the teacher uploads the recording manually
    requiresCredentials: false,
  },
  configSchema: [
    {
      key: "domain",
      label: "Dominio Jitsi",
      type: "text",
      required: false,
      placeholder: DEFAULT_JITSI_DOMAIN,
      help: "Servidor Jitsi propio; por defecto meet.jit.si.",
      default: DEFAULT_JITSI_DOMAIN,
    },
  ],
};

export const jitsiProvider: MeetingProvider = {
  manifest,
  buildJoin(session: LiveSession, cfg: Record<string, unknown>, user: JoinUser): JoinDescriptor {
    const domainRaw =
      typeof cfg.domain === "string" && cfg.domain.trim() !== ""
        ? cfg.domain.trim()
        : DEFAULT_JITSI_DOMAIN;
    // The salt is provided by the server; without it, the room isn't guessable. An empty
    // salt would degrade determinism, so we require its presence.
    const salt = session.roomSalt ?? "";
    return {
      mode: "embed",
      provider: "jitsi",
      domain: domainRaw,
      roomName: jitsiRoomName(session.id, salt),
      displayName: user.name?.trim() || undefined,
    };
  },
  // Lazy load of the iframe component (implemented in F3-G, features/live).
  // Only the contract is declared here; the real consumer will do the dynamic import.
};
